import { Box, Grid } from '@mui/material';
import { useApi, useDialog, useMessage } from 'app/context';
import { useForm } from 'app/hooks';
import {
  Breadcrumb,
  Button,
  DetailWrapperDiv,
  InputDatePicker,
  InputDateTimePicker,
  InputSearchField,
  SearchDoctor,
  SearchPatient,
  TextField,
} from 'app/shared';
import { buildErrorDetails, buildErrorMessage } from 'app/utils';
import { IDoctorSchema, IPatientSchema, IReportCreate } from 'generated/axios';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const ReportCreate = () => {
  const { reportApi } = useApi();
  const [loading, setLoading] = useState<boolean>(false);
  const { createDialog, closeDialog } = useDialog();
  const { success, error } = useMessage();
  const navigate = useNavigate();

  const [selectedDoctor, setSelectedDoctor] = useState<IDoctorSchema | undefined>();
  const [selectedPatient, setSelectedPatient] = useState<IPatientSchema | undefined>();
  const [defaultValues] = useState<IReportCreate>({
    temperature: t('default.temperature'),
    urine_quantity: t('default.urineQuantity'),
  });

  //updates request with selected doctor
  useEffect(() => {
    setValue({ field: 'doctor_id', value: selectedDoctor?.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDoctor]);

  //updates request with selected patient
  useEffect(() => {
    setValue({ field: 'patient_id', value: selectedPatient?.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient]);

  const create = (form: IReportCreate) => {
    setLoading(true);
    reportApi
      .webCreateReport(form)
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        success({ message: t('report.createSuccess') as string });
        navigate(-1);
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
        setErrors(buildErrorDetails(err));
      })
      .finally(() => setLoading(false));
  };

  const { bindTextField, setValue, form, submit, setErrors } = useForm<IReportCreate>({
    defaultValues: defaultValues,
    onSubmit: create,

    requiredValidationOptions: { errorMsg: t('info.mandatoryField'), validationTime: 'ON_SUBMIT' },
  });

  //search doctor dialog
  const handleDoctorDialog = () =>
    createDialog({
      open: true,
      maxWidth: 'md',
      fullWidth: true,
      children: (
        <SearchDoctor
          onCancel={closeDialog}
          onConfirmDoctor={(doctor) => {
            setSelectedDoctor(doctor);
            closeDialog();
          }}
        />
      ),
    });

  const handleCancelDoctor = () => setSelectedDoctor(undefined);

  //search patient dialog
  const handlePatientDialog = () =>
    createDialog({
      open: true,
      maxWidth: 'md',
      fullWidth: true,
      children: (
        <SearchPatient
          onCancel={closeDialog}
          onConfirmPatient={(patient) => {
            setSelectedPatient(patient);
            closeDialog();
          }}
        />
      ),
    });

  const handleCancelPatient = () => setSelectedPatient(undefined);

  return (
    <>
      <Breadcrumb />
      <DetailWrapperDiv>
        <Grid container spacing={4}>
          <Grid size={6}>
            <InputSearchField
              label={t('common.patient')}
              fieldValue={(selectedPatient?.last_name ?? '') + ' ' + (selectedPatient?.first_name ?? '')}
              onSearch={handlePatientDialog}
              onCancel={handleCancelPatient}
            />
          </Grid>
          <Grid size={6}>
            <InputSearchField
              label={t('common.doctor')}
              fieldValue={(selectedDoctor?.last_name ?? '') + ' ' + (selectedDoctor?.first_name ?? '')}
              onSearch={handleDoctorDialog}
              onCancel={handleCancelDoctor}
            />
          </Grid>
          <Grid size={6}>
            <TextField label={t('report.temperature')} {...bindTextField({ field: 'temperature' })} />
          </Grid>
          <Grid size={6}>
            <TextField label={t('report.urineQuantity')} {...bindTextField({ field: 'urine_quantity' })} />
          </Grid>
          <Grid size={6}>
            <InputDateTimePicker
              label={t('report.testExecutionDate')}
              defaultValue={null}
              onDatePick={(value) => setValue({ field: 'test_execution_date', value: value })}
            />
          </Grid>
          <Grid size={6}>
            <InputDateTimePicker
              label={t('report.reportingDate')}
              defaultValue={null}
              onDatePick={(value) => setValue({ field: 'report_date', value: value })}
            />
          </Grid>
          <Grid size={12}>
            <TextField label={t('report.notes')} multiline rows={3} {...bindTextField({ field: 'comment' })} />
          </Grid>
        </Grid>
        <Box padding={4}>
          <Button sx={{ margin: 1 }} variant="contained" loading={loading} onClick={submit} disabled={!form.patient_id}>
            {t('actions.create')}
          </Button>
        </Box>
      </DetailWrapperDiv>
    </>
  );
};
